'use client';

import { FormField } from '@modules/prism-client/components/elements/FormField';
import { apiTextButtonToTextButtonProps } from '@modules/prism-client/components/elements/TextButton';
import { telemetryToFormAttributes } from '@modules/prism-client/lib/telemetry';
import { documentToReactTree } from '@tuic/contentful';
import { Form as WebCompositionsForm } from '@tuic/web-compositions/shared/Form';

import { apiButtonToButtonProps } from './Button';

import type PrismAPI from '@modules/prism-client/types/prism-types';
import type { FormProps as WebCompositionsFormProps } from '@tuic/web-compositions/shared/Form';
import type { FC } from 'react';

export type FormProps = Omit<
  WebCompositionsFormProps,
  'submitButton' | 'cancelButton'
> & {
  data: PrismAPI.Form;
  fieldErrors?: Record<string, string>;
};

export const Form: FC<FormProps> = ({ data, fieldErrors, ...props }) => {
  const subline = data.header?.subline
    ? documentToReactTree(data.header.subline)
    : undefined;

  const header = {
    headline: data.header?.headline ?? props.header?.headline,
    subline: subline ?? props.header?.subline,
  };

  return (
    <WebCompositionsForm
      noValidate
      {...telemetryToFormAttributes(data.telemetry)}
      header={header}
      submitButton={apiButtonToButtonProps({ data: data.submitButton })}
      cancelButton={
        data.cancelButton
          ? apiTextButtonToTextButtonProps({ data: data.cancelButton })
          : undefined
      }
      {...props}
    >
      {props.children ?? getChildren(data.fields, fieldErrors)}
    </WebCompositionsForm>
  );
};

const getChildren = (
  fields: PrismAPI.FormField[],
  fieldErrors?: Record<string, string>,
): WebCompositionsFormProps['children'] => {
  return state => {
    return fields.map((field, idx) => {
      const disabled = state.pending || state.disabled;

      return (
        <FormField
          key={idx}
          data={field}
          disabled={disabled ? disabled : field.disabled}
          error={fieldErrors?.[field.name]}
        />
      );
    });
  };
};
